// TradingComponent.js
import React, { useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { usePermitted } from "../common/components/permissions/permissions";
import Loading from "../common/components/loading/loading";
import TradingLayout from "./tradingLayout";
import tradingService, {
  hasTradingHubAccess,
  TRADING_HUB_ROLES,
} from "../common/services/tradingEngine/tradingService";
import { getOrganizationsByUser } from "../common/services/auth/userAccountOrganizationService";

const TradingComponent = () => {
  const { keycloak, initialized } = useKeycloak();
  const [connected, setConnected] = useState(false);

  const [marketControl, setMarketControl] = useState([]);
  const [marketsOpen, setMarketsOpen] = useState(null);
  const [marketInstruments, setMarketInstruments] = useState([]);
  const [marketOrders, setMarketOrders] = useState([]);
  const [marketTrades, setMarketTrades] = useState([]);
  const [organizationOrders, setOrganizationOrders] = useState([]);
  const [organizationTrades, setOrganizationTrades] = useState([]);
  const [userOrganizationIds, setUserOrganizationIds] = useState([]);
  const [tradeConfirmationNotification, setTradeConfirmationNotification] =
    useState(null);

  const canConnectTradingHub = usePermitted(TRADING_HUB_ROLES);
  const tradeOnBehalf = usePermitted(["operator", "administrator"]);
  const tradeAllowed = usePermitted([
    "trading-manage-orders",
    "operator",
    "administrator",
  ]);
  const userAccountId =
    initialized && keycloak.tokenParsed?.OriginatingUserAccountID;
  const configuredMarkets = marketControl.filter((market) => market.active);

  const messageHub = process.env.REACT_APP_TRADING_ENGINE + "hub/trade";

  useEffect(() => {
    if (!initialized || !keycloak?.token) return;

    if (!canConnectTradingHub || !hasTradingHubAccess(keycloak)) {
      setConnected(false);
      return;
    }

    let isMounted = true;

    setConnected(false);

    tradingService.initialize({
      keycloak,
      messageHub,
      setters: {
        setMarketControl,
        setMarketsOpen,
        setMarketInstruments,
        setMarketOrders,
        setMarketTrades,
        setOrganizationOrders,
        setOrganizationTrades,
        setTradeConfirmationNotification,
      },
    });

    tradingService
      .connect()
      .then(() => {
        if (isMounted) {
          setConnected(true);
        }
      })
      .catch((error) => {
        console.error(
          "TradingComponent failed to attach to trading hub:",
          error,
        );
      });

    return () => {
      isMounted = false;
    };
  }, [canConnectTradingHub, initialized, keycloak, messageHub]);

  useEffect(() => {
    if (!initialized || !userAccountId) {
      setUserOrganizationIds([]);
      return;
    }

    if (tradeOnBehalf) {
      setUserOrganizationIds([]);
      return;
    }

    let isMounted = true;

    getOrganizationsByUser(userAccountId)
      .then((organizations) => {
        if (isMounted) {
          setUserOrganizationIds(
            organizations
              .filter((organization) => organization.active)
              .map((organization) => organization.organizationId),
          );
        }
      })
      .catch((error) => {
        console.error(
          "TradingComponent failed to load user organizations:",
          error,
        );
        if (isMounted) {
          setUserOrganizationIds([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [initialized, tradeOnBehalf, userAccountId]);

  if (!canConnectTradingHub) {
    return null;
  }

  if (!connected) {
    return <Loading />;
  }

  if (!configuredMarkets.length) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <div className="text-muted small">No Markets Configured for Trading</div>
      </div>
    );
  }

  return (
    <TradingLayout
      marketControl={marketControl}
      marketsOpen={marketsOpen}
      marketInstruments={marketInstruments}
      marketOrders={marketOrders}
      marketTrades={marketTrades}
      organizationOrders={organizationOrders}
      organizationTrades={organizationTrades}
      userAccountId={userAccountId}
      userOrganizationIds={userOrganizationIds}
      tradeOnBehalf={tradeOnBehalf}
      tradeAllowed={tradeAllowed}
      tradeConfirmationNotification={tradeConfirmationNotification}
      submitOrder={(order) => tradingService.submitOrder(order)}
      modifyOrder={(order) => tradingService.modifyOrder(order)}
      withdrawOrder={(order) => tradingService.withdrawOrder(order)}
      pickOrder={(order) => tradingService.pickOrder(order)}
    />
  );
};

export default TradingComponent;
